import { Inject, Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { LogedUser } from './models/logged-user.model';
import { canAccessDashboard } from './dashboard.guard';
import { AuthSession } from './auth-flow/auth-session';
import { AUTH_SESSION } from './auth-flow/tokens';

const OFFICE_CHANGE_SESSION_KEY = 'gesOfficeChangeSession';
const POST_LOGIN_REDIRECT_APPLIED_KEY = 'gesPostLoginRedirectApplied';

export function markOfficeChangeSession(): void {
  sessionStorage.setItem(OFFICE_CHANGE_SESSION_KEY, 'true');
}

export function hasOfficeChangeSession(): boolean {
  return sessionStorage.getItem(OFFICE_CHANGE_SESSION_KEY) === 'true';
}

export function consumeOfficeChangeSession(): boolean {
  const IS_OFFICE_CHANGE = hasOfficeChangeSession();

  if (IS_OFFICE_CHANGE) {
    sessionStorage.removeItem(OFFICE_CHANGE_SESSION_KEY);
  }

  return IS_OFFICE_CHANGE;
}

export function hasPostLoginRedirectApplied(): boolean {
  return sessionStorage.getItem(POST_LOGIN_REDIRECT_APPLIED_KEY) === 'true';
}

export function markPostLoginRedirectApplied(): void {
  sessionStorage.setItem(POST_LOGIN_REDIRECT_APPLIED_KEY, 'true');
}

export function clearPostLoginRedirectState(): void {
  sessionStorage.removeItem(OFFICE_CHANGE_SESSION_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_APPLIED_KEY);
}

export function shouldApplyPostLoginRedirect(user: LogedUser): boolean {
  if (hasPostLoginRedirectApplied()) {
    return false;
  }

  return isNewUserSession(user);
}

export function isNewUserSession(user: LogedUser): boolean {
  const MESSAGE_ID: string | null = localStorage.getItem('messagesId');

  return MESSAGE_ID != user.token;
}

export function markUserSession(user: LogedUser): void {
  user.haveMessage
    ? localStorage.setItem('showMessage', 'true')
    : localStorage.setItem('showMessage', 'false');
  localStorage.setItem('messagesId', user.token);
}

@Injectable({ providedIn: 'root' })
export class PostLoginRedirectGuard implements CanActivate {
  constructor(
    private readonly router: Router,
    @Inject(AUTH_SESSION) private readonly authSession: AuthSession,
  ) {}

  canActivate(): Promise<boolean | UrlTree> {
    // restoreSession resolves (and stores) the user for the active login flow
    return this.authSession
      .restoreSession()
      .then((user: LogedUser) => {
        if (hasOfficeChangeSession()) {
          consumeOfficeChangeSession();
          markUserSession(user);
          markPostLoginRedirectApplied();
          return true;
        }

        const IS_NEW_SESSION = isNewUserSession(user);

        if (IS_NEW_SESSION) {
          markUserSession(user);
        }

        if (
          shouldApplyPostLoginRedirect(user) &&
          canAccessDashboard(user)
        ) {
          markPostLoginRedirectApplied();
          return this.router.createUrlTree(['/dashboard']);
        }

        return true;
      })
      .catch(() => true);
  }
}
